/**
 * Rhythmz — SceneLighting
 * ────────────────────────
 * A 3-point lighting rig for 3D scenes. Exposes a THREE.Group that can be added
 * to any scene group. Lights are audio-reactive:
 *   - Key light: bright, warm, intensity swells with beat
 *   - Fill light: dim, cool, complements key
 *   - Rim light: colored, from behind, flashes on bass
 *   - Camera PointLight: headlamp effect, pulses with level
 *
 * Each scene creates its own SceneLighting instance with per-scene color config.
 */

import * as THREE from 'three/webgpu';

export interface SceneLightingConfig {
  keyColor?: THREE.Color;     // default warm white
  keyIntensity?: number;      // default 8
  keyPosition?: THREE.Vector3; // default (5, 5, 5)
  fillColor?: THREE.Color;    // default cool blue
  fillIntensity?: number;     // default 4
  fillPosition?: THREE.Vector3; // default (-4, 2, 3)
  rimColor?: THREE.Color;     // default colored rim
  rimIntensity?: number;      // default 6
  rimPosition?: THREE.Vector3; // default (-2, -3, -5)
  camLightIntensity?: number; // default 2
}

export interface AudioRef {
  bass: number;
  beat: number;
  level: number;
  mid: number;
}

const _white = new THREE.Color(1.0, 0.95, 0.85);
const _coolBlue = new THREE.Color(0.3, 0.5, 0.9);
const _warmOrange = new THREE.Color(1.0, 0.6, 0.2);

export class SceneLighting {
  group: THREE.Group;
  keyLight: THREE.DirectionalLight;
  fillLight: THREE.DirectionalLight;
  rimLight: THREE.DirectionalLight;
  camLight: THREE.PointLight;
  ambient: THREE.AmbientLight;

  private config: Required<SceneLightingConfig>;

  constructor(config?: SceneLightingConfig) {
    const def = {
      keyColor: _white.clone(),
      keyIntensity: 8,
      keyPosition: new THREE.Vector3(5, 5, 5),
      fillColor: _coolBlue.clone(),
      fillIntensity: 4,
      fillPosition: new THREE.Vector3(-4, 2, 3),
      rimColor: _warmOrange.clone(),
      rimIntensity: 6,
      rimPosition: new THREE.Vector3(-2, -3, -5),
      camLightIntensity: 2,
    };
    this.config = { ...def, ...config };

    this.group = new THREE.Group();

    // Ambient for base visibility — dim so 3-point does the heavy lifting
    this.ambient = new THREE.AmbientLight(0x222244, 0.4);
    this.group.add(this.ambient);

    // Key: main directional light
    this.keyLight = new THREE.DirectionalLight(this.config.keyColor, this.config.keyIntensity);
    this.keyLight.position.copy(this.config.keyPosition);
    this.group.add(this.keyLight);

    // Fill: opposite side, cooler, half intensity
    this.fillLight = new THREE.DirectionalLight(this.config.fillColor, this.config.fillIntensity);
    this.fillLight.position.copy(this.config.fillPosition);
    this.group.add(this.fillLight);

    // Rim: from behind, colored, bright — gives the Excision edge glow
    this.rimLight = new THREE.DirectionalLight(this.config.rimColor, this.config.rimIntensity);
    this.rimLight.position.copy(this.config.rimPosition);
    this.group.add(this.rimLight);

    // Camera light: subtle point light at camera position for front fill
    this.camLight = new THREE.PointLight(0xffffff, this.config.camLightIntensity, 15);
    this.group.add(this.camLight);
  }

  /** Call each frame with audio data for reactivity. */
  update(audio: AudioRef): void {
    const b = audio.bass;
    const beat = audio.beat;
    const level = audio.level;

    // Key light swells on beat
    this.keyLight.intensity = this.config.keyIntensity * (1 + beat * 0.5);
    // Rim light pulses with bass
    this.rimLight.intensity = this.config.rimIntensity * (1 + b * 0.8);
    // Cam light intensity follows overall level
    this.camLight.intensity = this.config.camLightIntensity * (0.5 + level * 0.8);
    // Fill stays steady
    this.fillLight.intensity = this.config.fillIntensity * (0.8 + b * 0.2);
  }

  /** Set a new color scheme dynamically (e.g., on scene change). */
  setColors(key: THREE.Color, fill: THREE.Color, rim: THREE.Color): void {
    this.keyLight.color.copy(key);
    this.fillLight.color.copy(fill);
    this.rimLight.color.copy(rim);
  }

  dispose(): void {
    this.group.clear();
  }
}